import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnShape } from 'react-base-table';
import { flatMap, cloneDeep } from 'lodash';

import {
  useDeleteFlowById,
  useDeploy,
  useGetFlowDetailsById,
  useGetFlowsManagementList,
  useUndeploy,
} from '@src/emp-3.0/api/queryHooks';
import { useDeleteVersion } from '@components/metrics-builder/api/queryHooks';
import { DeleteVersionType } from '@src/emp-3.0/components/Management/components/BulkAction/typed';
import { generateSelectOptions } from '@src/emp-3.0/utils/buildOptions';
import { useCreatePermissions } from '@hooks/usePermissions';
import { IFlowList } from '@src/emp-3.0/domain/flow/types';
import { statusOptions } from '@src/emp-3.0/utils/constants';
import {
  DEFAULT_SELECT_VALUE,
  dateFields,
  handleAction,
  supportSearchFields,
  FLOW,
} from '@src/emp-3.0/constants/management';
import { getFilteredSortedData } from '@src/emp-3.0/utils/management';
import { isDraftVersion, isMultiVersion } from '@src/emp-3.0/utils';

import {
  ManagementTable,
  useFlowsManagementStore,
  generateColumn,
  managementTableStyles,
  SearchFilterPanel,
  BulkAction,
} from '../';
import classnames from 'classnames';
import { useNewDataModel } from '@hooks/useNewDataModel';
import { workbenchModeTrueButNotWorkbenchAsset } from '../../WorkbenchButton/util';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

const Flow = () => {
  const navigate = useNavigate();
  const hasCreate = useCreatePermissions();
  const isNewDataModel = useNewDataModel();

  const {
    // Tab related state
    openBulkAction,
    setOpenBulkAction,

    // Multiple selection operation
    selectedList,
    setSelectedList,
    setRemoveInvalidSelectedList,

    // Search
    searchText,

    // Filter conditions
    showFilter,
    setShowFilter,
    resetFilter,
    setSearchText,
    status,
    updateDate,
    deployDate,
    createdBy,
    updatedBy,
    setStatus,
    setUpdateDate,
    setDeployDate,
    setCreatedBy,
    setUpdatedBy,

    // Sort state
    sort,
    setSort,

    // Total Count related
    totalCount,
    setTotalCount,
  } = useFlowsManagementStore((state) => state);

  const [flowId, setFlowId] = useState<number | undefined>(undefined);
  const [dataSource, setDataSource] = useState<any[]>([]);

  const { data: flowList, isFetching: isLoadingFlowList } = useGetFlowsManagementList();
  const { data: flowDetails } = useGetFlowDetailsById(flowId);
  const { mutate: deployFlow, isLoading: isLoadingDeploy } = useDeploy();
  const { mutate: undeployFlow, isLoading: isLoadingUndeploy } = useUndeploy();
  const { mutate: deleteFlow, isLoading: isLoadingDelete } = useDeleteFlowById();
  const { mutate: deleteVersions } = useDeleteVersion();
  const { workbenchMode } = useInitialInfoStore();
  const isLoading = useMemo(
    () => isLoadingFlowList || isLoadingDeploy || isLoadingUndeploy || isLoadingDelete,
    [isLoadingFlowList, isLoadingDeploy, isLoadingUndeploy, isLoadingDelete],
  );

  const { createdByOptions, updatedByOptions } = useMemo(
    () => generateSelectOptions(flowList, DEFAULT_SELECT_VALUE),
    [flowList],
  );

  const filteredSortedData = useMemo(
    () =>
      getFilteredSortedData<IFlowList>({
        dataList: flowList,
        filterParams: {
          status,
          updateDate,
          deployDate,
          createdBy,
          updatedBy,
        },
        searchParams: {
          searchText,
          supportSearchFields,
          dateFields,
        },
        sortParams: sort,
      }),
    [createdBy, deployDate, flowList, searchText, sort, status, updateDate, updatedBy],
  );

  const tableData = useMemo(
    () =>
      flatMap<IFlowList, any>(filteredSortedData, (element, index) =>
        element.versions.map((version) => {
          const id = `${element.flow_id}_${version.version_id}`;
          const is_multi_version = element.versions.length > 1;

          return {
            ...version,
            id,
            flow_id: element.flow_id,
            row_number: index,
            is_multi_version,
            expandedState: {
              // expanded object key is column dataKey
              associated_dimension_names: true,
              related_events: true,
            },
          };
        }),
      ),
    [filteredSortedData],
  );

  useEffect(() => {
    setDataSource(cloneDeep(tableData));
  }, [tableData]);

  useEffect(() => {
    setRemoveInvalidSelectedList(dataSource);
  }, [setRemoveInvalidSelectedList, dataSource]);

  const handleNameClick = ({ rowData }) => {
    setFlowId(rowData.flow_id);
  };

  const handleToggleExpanded = useCallback(
    ({ rowData }) => {
      dataSource.forEach((d) => {
        if (d.id === rowData?.id) d.expandedState = rowData?.expandedState;
      });
      setDataSource([...dataSource]);
    },
    [dataSource],
  );

  const handleDeployFlow = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        handleAction({
          action: deployFlow,
          params: {
            resource: 'flow',
            payload: {
              deploy_all: false,
              ids: [rowData.flow_id],
            },
          },
          successMessage: 'Deploy Successfully!',
        });
      }
    },
    [deployFlow],
  );

  const handleUndeployFlow = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        handleAction({
          action: undeployFlow,
          params: {
            resource: 'flow',
            payload: {
              ids: [rowData.flow_id],
            },
          },
          successMessage: 'Undeploy Successfully!',
        });
      }
    },
    [undeployFlow],
  );

  const handleDeleteFlow = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        const isSubVersion = isMultiVersion(rowData) && isDraftVersion(rowData);
        if (isSubVersion) {
          // TODO: Need to add an API for deleting drafts.
          handleAction({
            action: deleteVersions,
            params: { type: DeleteVersionType.flow, ids: [rowData.version_id] },
            successMessage: 'Delete Successfully!',
          });
        } else {
          handleAction({
            action: deleteFlow,
            params: rowData.flow_id,
            successMessage: 'Delete Successfully!',
          });
        }
      }
    },
    [deleteFlow],
  );

  const columns = useMemo<ColumnShape<any>[]>(() => {
    const columnDefinitions: any[] = [
      {
        columnType: 'checkbox',
        dataKey: 'flow_name',
        selectedList,
        onChange: ({ selectedList }) => {
          setOpenBulkAction(selectedList.length > 0);
          setSelectedList(selectedList);
        },
        data: dataSource,
        disable: (rowData) => workbenchModeTrueButNotWorkbenchAsset(rowData?.is_beta),
      },
      {
        columnType: 'number',
      },
      {
        columnType: 'name',
        title: 'Flows',
        key: 'flow',
        dataKey: 'flow_name',
        sort,
        onSortChange: setSort,
        onClick: handleNameClick,
      },
      {
        columnType: 'status',
        options: statusOptions,
        value: status,
        onChange: setStatus,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'list',
        title: 'Mapped Event Names',
        key: 'mapped-event-names',
        dataKey: 'related_events',
        onClick: handleToggleExpanded,
      },
      ...(isNewDataModel
        ? [
            {
              columnType: 'list',
              title: 'Associated Dimension',
              key: 'associated_dimension_names',
              dataKey: 'associated_dimension_names',
              width: 230,
              onClick: handleToggleExpanded,
            },
          ]
        : []),
      {
        columnType: 'common',
        title: 'Created By',
        key: 'created by',
        dataKey: 'created_by',
        options: createdByOptions,
        value: createdBy,
        onChange: setCreatedBy,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'common',
        title: 'Updated By',
        key: 'updated by',
        dataKey: 'updated_by',
        options: updatedByOptions,
        value: updatedBy,
        onChange: setUpdatedBy,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'date',
        title: 'Updated At',
        key: 'updated at',
        dataKey: 'updated_at',
        value: updateDate,
        onChange: setUpdateDate,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'date',
        title: 'Deployed At',
        key: 'deployed at',
        dataKey: 'deployed_at',
        value: deployDate,
        onChange: setDeployDate,
        showFilter,
        sort,
        onSortChange: setSort,
      },
    ];
    if (hasCreate) {
      columnDefinitions.push({
        columnType: 'actions',
        dataKey: 'flow_name',
        type: FLOW,
        cloneVisible: false,
        onDeploy: handleDeployFlow,
        onUndeploy: handleUndeployFlow,
        onDelete: handleDeleteFlow,
      });
    }

    return columnDefinitions.map((config) => generateColumn(config));
  }, [
    isNewDataModel,
    selectedList,
    dataSource,
    sort,
    setSort,
    status,
    setStatus,
    showFilter,
    createdByOptions,
    createdBy,
    setCreatedBy,
    updatedByOptions,
    updatedBy,
    setUpdatedBy,
    updateDate,
    setUpdateDate,
    deployDate,
    setDeployDate,
    hasCreate,
    setOpenBulkAction,
    setSelectedList,
    handleDeployFlow,
    handleUndeployFlow,
    handleDeleteFlow,
    workbenchMode,
  ]);

  useEffect(() => {
    setTotalCount(flowList?.length ?? 0);
  }, [flowList?.length, setTotalCount]);

  useEffect(() => {
    if (flowDetails && flowId) {
      if (flowDetails?.flow_definition_type === 'sql') {
        navigate(`/metric-builder/pattern/${flowId}`);
      } else {
        navigate(`/metric-builder/flow/${flowId}`);
      }
    }
  }, [flowDetails, flowId]);

  const handleSearch = (value) => {
    setSearchText(value);
  };
  const handleShowFilterChange = (value: boolean) => {
    if (!value) {
      resetFilter();
    }
    setShowFilter(value);
  };

  return (
    <ManagementTable
      loading={isLoading}
      data={dataSource}
      columns={columns}
      rowKey="id"
      estimatedRowHeight={50}
      ignoreFunctionInColumnCompare={false}
      header={
        <SearchFilterPanel
          leftComp={
            <>
              <BulkAction
                resource={FLOW}
                data={selectedList}
                show={openBulkAction}
                onClose={() => {
                  setOpenBulkAction(false);
                  setSelectedList([]);
                }}
              />
              {!openBulkAction && (
                <div style={{ fontWeight: 400 }}>{`Total ${totalCount} Flows`}</div>
              )}
            </>
          }
          searchValue={searchText}
          onSearch={handleSearch}
          filtersVisible={true}
          showFilters={showFilter}
          onShowFilterChange={handleShowFilterChange}
        />
      }
      rowClassName={({ rowData }) => {
        return classnames({
          [managementTableStyles.tableRowActive]: selectedList?.find(
            (item) => item.id === rowData.id,
          ),
        });
      }}
    />
  );
};

export default Flow;
